import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Bookmark,
  ExerciseGroup,
  type ExerciseGroup as ExerciseGroupT,
} from '@heraion/shared';

import type { ProgramApiFetch } from '../ProgramEditor/types';
import type { PickerBookmarks } from './pickerProps';

// Picker stores for hosts WITHOUT their own native stores (web-rnw). Mobile
// keeps its AsyncStorage-backed useBookmarks/useExerciseGroups (cache-first,
// offline) and does NOT use these; that duplication is
// tracked in next/anti-monster.md. These are the lean network-only versions:
// fetch once on mount, optimistic toggle, revert on failure.

const fetchBookmarkSlugs = async (
  apiFetch: ProgramApiFetch,
): Promise<string[]> => {
  const data = await apiFetch<{ items?: unknown[] }>('/api/me/bookmarks');
  const out: string[] = [];
  for (const raw of data.items ?? []) {
    const parsed = Bookmark.safeParse(raw);
    if (parsed.success) out.push(parsed.data.exerciseSlug);
  }
  return out;
};

export function useBookmarksStore(apiFetch: ProgramApiFetch): PickerBookmarks {
  const apiRef = useRef(apiFetch);
  useEffect(() => {
    apiRef.current = apiFetch;
  }, [apiFetch]);
  const [slugs, setSlugs] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    let cancelled = false;
    void fetchBookmarkSlugs(apiRef.current)
      .then((list) => {
        if (!cancelled) setSlugs(new Set(list));
      })
      .catch(() => {
        // Offline / fetch failure: stars simply start unpinned this session.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const toggle = useCallback((slug: string) => {
    setSlugs((prev) => {
      const adding = !prev.has(slug);
      const next = new Set(prev);
      if (adding) next.add(slug);
      else next.delete(slug);
      void (async () => {
        try {
          if (adding) {
            await apiRef.current('/api/me/bookmarks', {
              method: 'POST',
              body: { exerciseSlug: slug },
            });
          } else {
            await apiRef.current(
              `/api/me/bookmarks/${encodeURIComponent(slug)}`,
              { method: 'DELETE' },
            );
          }
        } catch {
          // Revert the optimistic flip on failure.
          setSlugs((cur) => {
            const reverted = new Set(cur);
            if (adding) reverted.delete(slug);
            else reverted.add(slug);
            return reverted;
          });
        }
      })();
      return next;
    });
  }, []);

  return {
    isBookmarked: useCallback((slug: string) => slugs.has(slug), [slugs]),
    toggle,
    count: slugs.size,
  };
}

export function useExerciseGroupsStore(apiFetch: ProgramApiFetch): {
  groups: ExerciseGroupT[];
  removeGroup: (id: string) => void;
} {
  const apiRef = useRef(apiFetch);
  useEffect(() => {
    apiRef.current = apiFetch;
  }, [apiFetch]);
  const [groups, setGroups] = useState<ExerciseGroupT[]>([]);

  useEffect(() => {
    let cancelled = false;
    void apiRef
      .current<{ items?: unknown[] }>('/api/me/exercise-groups')
      .then((data) => {
        if (cancelled) return;
        const out: ExerciseGroupT[] = [];
        for (const raw of data.items ?? []) {
          const parsed = ExerciseGroup.safeParse(raw);
          if (parsed.success) out.push(parsed.data);
        }
        setGroups(out);
      })
      .catch(() => {
        // Offline / fetch failure: the templates section just stays empty.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const removeGroup = useCallback((id: string) => {
    setGroups((prev) => prev.filter((g) => g.id !== id));
    void apiRef
      .current(`/api/me/exercise-groups/${encodeURIComponent(id)}`, {
        method: 'DELETE',
      })
      .catch(() => {
        // Deletion is idempotent housekeeping; a failed delete resurfaces
        // the template on the next mount.
      });
  }, []);

  return { groups, removeGroup };
}
